/**
 * Product filter URL state management via nuqs.
 *
 * Each filter maps to a URL query parameter so filter states are
 * shareable and bookmarkable. We use the Shopify Storefront API
 * `collection.products` endpoint with `ProductFilter` input for
 * server-side filtering (type, color, size, price, availability).
 */
import { parseAsArrayOf, parseAsFloat, parseAsString } from "nuqs";

// ── Build Shopify ProductFilter array from active filters ────────────

import type { ProductFilter } from "@shopify/hydrogen-react/storefront-api-types";

// ── nuqs parsers (define shape + defaults for each filter) ──────────

export const filterParsers = {
  /** Multi-select product types, e.g. ?type=Sandale&type=Remeni */
  type: parseAsArrayOf(parseAsString).withDefault([]),

  /** Multi-select colors (color-pattern metafield values) */
  color: parseAsArrayOf(parseAsString).withDefault([]),

  /** Multi-select sizes (EU shoe sizes or "one-size") */
  size: parseAsArrayOf(parseAsString).withDefault([]),

  /** Price range min */
  minPrice: parseAsFloat.withDefault(0),

  /** Price range max (0 = no upper bound) */
  maxPrice: parseAsFloat.withDefault(0),

  /** Gender filter */
  gender: parseAsString.withDefault(""),
} as const;

export type FilterParams = {
  [K in keyof typeof filterParsers]: ReturnType<
    (typeof filterParsers)[K]["parseServerSide"]
  >;
};

// ── Category-group mapping for context-aware filter visibility ──────

export const CATEGORY_GROUPS = {
  footwear: [
    "Handmade shoes",
    "Personalized handmade shoes",
    "Kožne papuče",
    "Sandale",
  ],
  bagsAndCases: [
    "Bag",
    "Backpack",
    "Torbe",
    "Eyewear Cases",
    "kožna galanterija",
  ],
  accessories: ["Keychains", "Remeni"],
  pet: ["Ljubimci"],
} as const;

export type CategoryGroup = keyof typeof CATEGORY_GROUPS;

/**
 * Determine which optional filter sections to show based on the
 * currently selected product types.
 */
export function getVisibleFilters(selectedTypes: Array<string>): {
  showColor: boolean;
  showSize: boolean;
} {
  if (selectedTypes.length === 0) {
    return { showColor: true, showSize: true };
  }

  const hasFootwear = selectedTypes.some((t) =>
    (CATEGORY_GROUPS.footwear as ReadonlyArray<string>).includes(t),
  );
  const hasBagsOrCases = selectedTypes.some((t) =>
    (CATEGORY_GROUPS.bagsAndCases as ReadonlyArray<string>).includes(t),
  );
  const hasAccessories = selectedTypes.some((t) =>
    (CATEGORY_GROUPS.accessories as ReadonlyArray<string>).includes(t),
  );
  const hasPetOnly =
    selectedTypes.length > 0 &&
    selectedTypes.every((t) =>
      (CATEGORY_GROUPS.pet as ReadonlyArray<string>).includes(t),
    );

  if (hasPetOnly) {
    return { showColor: false, showSize: false };
  }

  return {
    showColor: hasFootwear || hasBagsOrCases || hasAccessories,
    showSize: hasFootwear,
  };
}

/**
 * The variant option name for shoe sizes in the Shopify catalog.
 * Confirmed via Storefront API filter response: the option name is lowercase "size".
 */
const SIZE_OPTION_NAME = "size";

/**
 * Maps lowercase color labels to Shopify TaxonomyValue GIDs for the
 * `taxonomyMetafield` filter on `shopify.color-pattern`.
 * IDs are stable Shopify global taxonomy values (not tenant-specific).
 */
const COLOR_TAXONOMY_MAP: Record<string, string> = {
  beige: "gid://shopify/TaxonomyValue/6",
  black: "gid://shopify/TaxonomyValue/1",
  blue: "gid://shopify/TaxonomyValue/2",
  brown: "gid://shopify/TaxonomyValue/7",
  clear: "gid://shopify/TaxonomyValue/17",
  gray: "gid://shopify/TaxonomyValue/8",
  green: "gid://shopify/TaxonomyValue/9",
  multicolor: "gid://shopify/TaxonomyValue/2865",
  navy: "gid://shopify/TaxonomyValue/15",
  pink: "gid://shopify/TaxonomyValue/11",
  red: "gid://shopify/TaxonomyValue/13",
  silver: "gid://shopify/TaxonomyValue/5",
  white: "gid://shopify/TaxonomyValue/3",
  yellow: "gid://shopify/TaxonomyValue/14",
};

/**
 * Converts the active filter state into a Shopify `ProductFilter[]`
 * for use with the `collection.products(filters:)` endpoint.
 *
 * Multiple entries of the same filter type are OR'd by Shopify;
 * entries of different types are AND'd.
 *
 * Color filtering uses the `taxonomyMetafield` filter type with
 * Shopify TaxonomyValue GIDs (from the `shopify.color-pattern` metafield).
 * Size filtering uses `variantOption` with option name "size" (lowercase).
 */
export function buildProductFilters(
  filters: FilterParams,
): Array<ProductFilter> {
  const result: Array<ProductFilter> = [];

  // Product type filter
  for (const t of filters.type) {
    result.push({ productType: t });
  }

  // Color filter — uses taxonomyMetafield with Shopify TaxonomyValue GIDs
  for (const color of filters.color) {
    const taxonomyGid = COLOR_TAXONOMY_MAP[color.toLowerCase()];
    if (taxonomyGid) {
      result.push({
        taxonomyMetafield: {
          namespace: "shopify",
          key: "color-pattern",
          value: taxonomyGid,
        },
      } as ProductFilter);
    }
  }

  // Size filter — variant option name "size" (lowercase, confirmed via Storefront API)
  for (const size of filters.size) {
    result.push(
      {
        variantOption: { name: SIZE_OPTION_NAME, value: size },
      },
      {
        variantOption: { name: "Shoe size", value: size }, // fallback for inconsistent option naming
      },
    );
  }

  // Price range filter
  if (filters.minPrice > 0 || filters.maxPrice > 0) {
    result.push({
      price: {
        ...(filters.minPrice > 0 ? { min: filters.minPrice } : {}),
        ...(filters.maxPrice > 0 ? { max: filters.maxPrice } : {}),
      },
    });
  }

  return result;
}

/**
 * Returns true when any filter is active (ignoring defaults).
 */
export function hasActiveFilters(filters: FilterParams): boolean {
  return (
    filters.type.length > 0 ||
    filters.color.length > 0 ||
    filters.size.length > 0 ||
    filters.minPrice > 0 ||
    filters.maxPrice > 0
  );
}

/** Default search params for products route — use in <Link search={…}> */
export const PRODUCTS_DEFAULT_SEARCH = {
  sort: "default",
  type: [] as Array<string>,
  color: [] as Array<string>,
  size: [] as Array<string>,
  minPrice: 0,
  maxPrice: 0,
  gender: "",
} as const;
